import * as React from 'react'
import { Box, Grid } from '@material-ui/core'
import { c, t } from 'ttag'
import { ReactComponent as IconFind } from '../../../assets/v3/icons/ranking/find.svg'
import { formatThousands } from '../../../common/helpers/translations'
import YumButton from '../../YumButton/YumButton'
import { isRtl } from '../../../i18nInit'
import YumAvgSumFilter from '../../YumAvgSumFilter/YumAvgSumFilter'
import { Typography } from '@material-ui/core'
import { YumRankButton } from './YumRankButton'
import {
  GroupView,
  ProgressType,
  Role,
  GoalWithProgress,
  UserProfileDto,
  DivisionDto,
  UserCompanyDto,
  DivisionId,
  GoalId,
  DivisionCategoryRow,
} from '../../../services/api.types'
import dayjs from '../../../dayjsConfig'
import { convertUTCToLocalDate } from '../../../common/helpers/date'
import { ActivityFilter, ActivityView } from '../../../views/Activity/store/ActivityReducer'
import YumGoalRankingJoinButtonWrapper from './YumGoalRankingJoinButtonWrapper'
import YumGoalRankingExportButtonWrapper from './YumGoalRankingExportButtonWrapper'
import YumActivityFilterWrapper from '../../YumActivityFilter/YumActivityFilterWrapper'
import YumSearchField from '../../YumSearchField/YumSearchField'
import { translateDivisionCategoryType } from '../../../views/TeamManagement/TeamManagement'
import YumManualEntryButtonAndDetailsWrapper from '../../YumCustomEntry/YumManualEntryButtonAndDetailsWrapper'

export interface IYumGoalRankingHeaderProps {
  goalDto: GoalWithProgress
  userDto: UserProfileDto
  divisions: DivisionDto[]
  countries: string[]
  divisionCategories: DivisionCategoryRow[]
  activityView: ActivityView
  groupView: GroupView
  groupSum: boolean
  isLegacyRanking: boolean
  searchValue?: string
  setSearchValue?: (value: string) => void
  shouldShowFindMe?: boolean
  isFindMeLoading?: boolean
  hasRoleInCompany(companyId: string, role: Role): boolean
  onFindMe: () => void
  activityFilter: ActivityFilter
  defaultActivityFilter: ActivityFilter
  token?: string
  totalCount: number
  totalTeamCount: number
  totalCountryCount: number
  showTabs?: boolean
  isSearchVisible?: boolean
  setIsSearchVisible?: (visible: boolean) => void
  /** Whether to use the 2026 layout refresh styling */
  compactLayout?: boolean
  hideTeamFilter?: boolean
  hideCountryFilter?: boolean
  hiddenCustomGroupCategoryIds?: string[]
  hideSearchAndFilter?: boolean
}

export interface IYumGoalRankingHeaderDispatchProps {
  setGroupSum(payload: { goalId: GoalId; groupSum: boolean }): void
  getCurrentGoalActivityList(payload?: {
    goalId: GoalId
    divisionId: DivisionId
    activityFilter?: ActivityFilter
    activityView?: ActivityView
    groupView?: GroupView
    groupSum?: boolean
    token?: string
  }): void
  getCurrentGoalActivityListClear(payload?: { goalId: GoalId; updateGroupView?: boolean }): void
}

interface UserProfileWithCompanies extends UserProfileDto {
  userCompanies: UserCompanyDto[]
}

export const YumGoalRankingHeader = (props: IYumGoalRankingHeaderProps & IYumGoalRankingHeaderDispatchProps) => {
  const {
    goalDto,
    userDto,
    divisions,
    countries,
    divisionCategories,
    activityView,
    groupView,
    groupSum,
    isLegacyRanking,
    searchValue = '',
    setSearchValue,
    shouldShowFindMe = false,
    isFindMeLoading = false,
    activityFilter,
    defaultActivityFilter,
    token,
    totalCount,
    hasRoleInCompany,
    onFindMe,
    getCurrentGoalActivityListClear,
    getCurrentGoalActivityList,
    setGroupSum,
    totalTeamCount,
    totalCountryCount,
    showTabs = false,
    isSearchVisible,
    setIsSearchVisible,
    compactLayout = false,
    hideTeamFilter,
    hideCountryFilter,
    hiddenCustomGroupCategoryIds = [],
    hideSearchAndFilter,
  } = props
  const { id: goalId, companyId, progressType, participating, divisionId } = goalDto

  const isAdmin = hasRoleInCompany(companyId, Role.ADMIN)
  const isUser = hasRoleInCompany(companyId, Role.USER)

  const userCompany = (userDto as UserProfileWithCompanies).userCompanies?.find(
    (uc: any) => uc.companyId === companyId,
  ) || { divisionId: '', country: '' }

  //const participantsCount = goalDto.participantsCount ? ` (${formatThousands(goalDto.participantsCount)})` : ''
  const participantsCount = totalCount ? ` (${formatThousands(totalCount)})` : ''
  const teamsCount = totalTeamCount ? ` (${formatThousands(totalTeamCount)})` : ''
  const countriesCount = totalCountryCount ? ` (${formatThousands(totalCountryCount)})` : ''

  const isPresentOrFutureGoal = dayjs(convertUTCToLocalDate(new Date(goalDto.endDate))).isAfter(new Date())

  // Check if this is a DivisionCategory view
  const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')
  const categoryId = isDivisionCategoryView ? activityView.replace('DIVISION_CATEGORY_', '') : null
  const divisionCategory =
    isDivisionCategoryView && divisionCategories ? divisionCategories.find((cat) => cat.id === categoryId) : null
  const divisionCategoryName = divisionCategory ? translateDivisionCategoryType(divisionCategory, true) : ''

  const title =
    isDivisionCategoryView && !isLegacyRanking ? (
      <>
        {divisionCategoryName}
        {teamsCount}
      </>
    ) : activityView === ActivityView.COUNTRY && !isLegacyRanking ? (
      <>
        {t`Countries`}
        {countriesCount}
      </>
    ) : activityView === ActivityView.GROUP && !isLegacyRanking ? (
      <>
        {t`Teams`}
        {teamsCount}
      </>
    ) : (
      <>
        {/* {c('caption').t`Participants`} */}
        {t`Individuals`}
        {participantsCount}
      </>
    )

  const hasTeams = !hideTeamFilter && !!(divisions && divisions.length)
  const hasCustomGroups =
    !!divisionCategories?.length &&
    divisionCategories.some(
      (cat) => !hiddenCustomGroupCategoryIds.includes(cat.id) && divisions?.some((d) => d.categoryId === cat.id),
    )
  const hasCountries = !hideCountryFilter && !!(countries && countries.length)

  let showFilterButton = false
  if (!hideSearchAndFilter) {
    // For individual and collective leaderboards, show filter if there are any visible teams, custom groups, or countries
    if ([ActivityView.INDIVIDUAL, ActivityView.COLLECTIVE].includes(activityView)) {
      showFilterButton = hasTeams || hasCustomGroups || hasCountries
    }
    // For Teams or Custom group leaderboards, show if there are any visible countries
    else if (
      activityView === ActivityView.GROUP ||
      ![ActivityView.INDIVIDUAL, ActivityView.COLLECTIVE, ActivityView.GROUP, ActivityView.COUNTRY].includes(
        activityView,
      )
    ) {
      showFilterButton = hasCountries
    }
  }

  const onSetGroupSum = (groupSum: boolean) => {
    if (isLegacyRanking) {
      getCurrentGoalActivityListClear({ goalId, updateGroupView: true })
      getCurrentGoalActivityList({ goalId, divisionId, activityFilter, activityView, groupView, groupSum, token })
    } else {
      setGroupSum({ goalId, groupSum })
    }
  }

  const handleSearchClear = React.useCallback(() => {
    if (searchValue === '') {
      // If search is already empty, close the search
      //setState((prev) => ({ ...prev, isSearchVisible: false }))
      setIsSearchVisible?.(false)
    } else {
      // If search has text, just clear it
      setSearchValue?.('')
    }
  }, [searchValue])

  const handleSearch = () => {
    if (isLegacyRanking) {
      onFindMe()
    } else {
      // For non-legacy mode, we just show the search panel
      // The actual "Find me" button is shown inside the search panel
      setIsSearchVisible?.(true)
    }
  }

  const handleFindMe = () => {
    if (!isFindMeLoading) {
      // Clear search value first if needed
      if (searchValue) {
        setSearchValue?.('')
      }
      onFindMe()
    }
  }

  const isIframeLeaderboard = token

  const showJoinButton = !isIframeLeaderboard && !participating && isUser && isPresentOrFutureGoal

  const showSearchButton = !hideSearchAndFilter && (!isSearchVisible || isLegacyRanking)
  const showAvgSumFilter = progressType !== ProgressType.ALL && activityView !== ActivityView.INDIVIDUAL

  const filterButton = showFilterButton && (
    <YumActivityFilterWrapper
      defaultActivityFilter={defaultActivityFilter}
      currentActivityFilter={activityFilter}
      countries={countries}
      divisions={divisions}
      divisionCategories={divisionCategories}
      activityView={activityView}
      groupView={groupView}
      country={userCompany.country}
      divisionId={userCompany.divisionId}
      isLegacyRanking={isLegacyRanking}
      goalId={goalId}
      goalDto={goalDto}
      token={token}
      compactLayout={compactLayout}
      hideTeamFilter={hideTeamFilter}
      hideCountryFilter={hideCountryFilter}
      hiddenCustomGroupCategoryIds={hiddenCustomGroupCategoryIds}
    />
  )

  const searchButton = showSearchButton && (
    <YumButton
      variant={!compactLayout ? 'contained' : 'outlined'}
      size="small"
      title={t`Search`}
      aria-label={t`Search`}
      style={compactLayout ? {} : !isRtl ? { marginLeft: '8px' } : { marginRight: '8px' }}
      onClick={handleSearch}
    >
      <IconFind style={{ width: '1rem', height: '1rem' }} />
      {compactLayout && (
        <Box
          display={{ xs: 'none', md: 'inline' }}
          style={isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }}
        >{t`Search`}</Box>
      )}
      {!compactLayout && isLegacyRanking ? (
        <span style={isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }}>{t`Find me`}</span>
      ) : null}
    </YumButton>
  )

  const avgSumFilterButton = showAvgSumFilter && (
    <YumAvgSumFilter groupSum={groupSum} setGroupSumFilter={onSetGroupSum} />
  )

  const joinButton = showJoinButton && (
    <YumGoalRankingJoinButtonWrapper goal={goalDto} isLegacyRanking={isLegacyRanking} addMargin={true} />
  )

  const manualEntryButton = (
    <YumManualEntryButtonAndDetailsWrapper
      goal={goalDto}
      compact={true}
      isMeView={true}
      style={isRtl ? { marginRight: '8px' } : { marginLeft: '8px' }}
    />
  )

  return isSearchVisible && setSearchValue ? (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginBottom: compactLayout ? '8px' : '1rem',
        //marginTop: showTabs ? '1rem' : '',
      }}
    >
      {shouldShowFindMe && (
        <YumButton
          size="small"
          onClick={handleFindMe}
          disabled={isFindMeLoading}
          showLoader={isFindMeLoading}
          immersiveLoader={true}
        >
          {t`Find me`}
        </YumButton>
      )}
      <YumSearchField
        value={searchValue}
        onChange={setSearchValue}
        placeholder={t`Search`}
        autoFocus
        alwaysShowClearButton
        onClear={handleSearchClear}
        isChallengeView={true}
        whiteBackground={!compactLayout}
      />
    </div>
  ) : (
    <>
      <Grid
        container={true}
        justifyContent="space-between"
        alignItems="center"
        style={{ minHeight: '1.875rem', marginBottom: compactLayout ? '8px' : '1rem' }}
        wrap="nowrap"
      >
        {compactLayout ? (
          <>
            <Grid
              item
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                padding: '4px',
                margin: '-4px',
              }}
            >
              {!isIframeLeaderboard ? (
                <>
                  {showJoinButton ? (
                    <YumGoalRankingJoinButtonWrapper
                      goal={goalDto}
                      isLegacyRanking={isLegacyRanking}
                      addMargin={false}
                      //marginRight={true}
                    />
                  ) : (
                    <YumRankButton
                      goal={goalDto}
                      activityView={activityView}
                      onClick={handleFindMe}
                      addMargin={false}
                    />
                  )}
                </>
              ) : null}
              {searchButton}
              {!isIframeLeaderboard ? filterButton : null}
            </Grid>

            <Grid item style={{ flexShrink: 0 }}>
              {!isIframeLeaderboard ? manualEntryButton : null}
              {isIframeLeaderboard ? (
                <div
                  //style={isRtl || compactLayout ? { marginRight: '-8px' } : { marginLeft: '-8px' }}
                  style={
                    compactLayout
                      ? isRtl
                        ? { marginLeft: '-8px' }
                        : { marginRight: '-8px' }
                      : !isRtl
                        ? { marginLeft: '-8px' }
                        : { marginRight: '-8px' }
                  }
                >
                  {filterButton}
                </div>
              ) : null}
            </Grid>
          </>
        ) : (
          <>
            <Grid item style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Typography variant="caption" style={{ margin: 0, whiteSpace: 'nowrap' }}>
                {title}
              </Typography>
            </Grid>
            <Grid item style={{ flexShrink: 0 }}>
              {joinButton}
              {avgSumFilterButton}
              {filterButton}
              {/* {isAdmin && !token ? <YumGoalRankingExportButtonWrapper goalId={goalId} /> : null} */}
              {searchButton}
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}
